import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ChallengeTimelineItemType } from 'features/challenge/ChallengeTimelineItem';
import { FocusAreaType, useGoalQuery } from 'features/goal';

type TGoalBreakdownData = {
  challenges: ChallengeTimelineItemType[];
  focusAreas: FocusAreaType[];
};

type TGoalBreakdownContext = {
  challenges: ChallengeTimelineItemType[];
  focusAreas: FocusAreaType[];
  setGoalBreakdownData: (
    updater:
      | TGoalBreakdownData
      | ((prevData: TGoalBreakdownData) => TGoalBreakdownData)
  ) => void;
};

const GoalBreakdownContext = createContext<TGoalBreakdownContext | undefined>(
  undefined
);

type TGoalBreakdownProvider = {
  children: React.ReactNode;
};

export const GoalBreakdownProvider = ({ children }: TGoalBreakdownProvider) => {
  const { data: goal, dataUpdatedAt } = useGoalQuery();
  const prevUpdatedAtRef = useRef(dataUpdatedAt);

  const [goalBreakdownData, setGoalBreakdownData] =
    useState<TGoalBreakdownData>({
      challenges: goal?.challenges || [],
      focusAreas: goal?.focusAreas || [],
    });

  const { challenges, focusAreas } = goalBreakdownData;

  useEffect(() => {
    if (dataUpdatedAt !== prevUpdatedAtRef.current) {
      setGoalBreakdownData({
        challenges: goal?.challenges || [],
        focusAreas: goal?.focusAreas || [],
      });

      prevUpdatedAtRef.current = dataUpdatedAt;
    }
  }, [goal, dataUpdatedAt]);

  const contextValue = useMemo(
    () => ({
      challenges,
      focusAreas,
      setGoalBreakdownData: (
        updater:
          | TGoalBreakdownData
          | ((prevData: TGoalBreakdownData) => TGoalBreakdownData)
      ) => {
        if (typeof updater === 'function') {
          setGoalBreakdownData((prev) => updater(prev));
        } else {
          setGoalBreakdownData(updater);
        }
      },
    }),
    [challenges, focusAreas]
  );

  return (
    <GoalBreakdownContext.Provider value={contextValue}>
      {children}
    </GoalBreakdownContext.Provider>
  );
};

export const useGoalBreakdown = () => {
  const context = useContext(GoalBreakdownContext);

  if (!context) {
    throw new Error(
      'useGoalBreakdown must be used within GoalBreakdownContext.Provider'
    );
  }

  return context;
};

export type { TGoalBreakdownData };
